import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { List, X } from "@phosphor-icons/react";
import { LogoImage } from "@/components/Logo";
import { useContent } from "@/context/ContentContext";
import { DEFAULTS } from "@/data/contentRegistry";

// Visible nav links. "Méthode" is intentionally hidden from the menu
// but the /methode route and page remain accessible.
const LINKS = [
  { to: "/", key: "nav.home" },
  { to: "/assurances-professionnelles", key: "nav.assurances" },
  { to: "/sante-prevoyance", key: "nav.sante" },
  { to: "/patrimoine-placements", key: "nav.patrimoine" },
  { to: "/partenaires", key: "nav.partenaires" },
  { to: "/contact", key: "nav.contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { content } = useContent();
  const tx = (k) => content?.[k] || DEFAULTS[k] || "";
  const cta = tx("header.cta");

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-ducs-night/90 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-28 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="flex items-center">
          <LogoImage className="h-20 md:h-24 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive ? "text-ducs-gold" : "text-ducs-cream/80 hover:text-ducs-gold"
                }`
              }
            >
              {tx(l.key)}
            </NavLink>
          ))}
        </nav>

        <Link to="/contact" data-testid="header-cta-contact" className="hidden lg:inline-flex btn-primary !py-2.5 !px-5">
          {cta}
        </Link>

        <button data-testid="mobile-menu-toggle" aria-label="Menu" onClick={() => setOpen(!open)} className="lg:hidden text-ducs-cream">
          {open ? <X size={26} /> : <List size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-ducs-night/95 backdrop-blur-xl border-t border-white/5">
          <div className="max-w-7xl mx-auto px-5 py-6 flex flex-col gap-4">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-ducs-gold" : "text-ducs-cream/85"}`
                }
              >
                {tx(l.key)}
              </NavLink>
            ))}
            <Link to="/contact" className="btn-primary w-fit mt-2" data-testid="mobile-cta-contact">
              {cta}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
